<template>
  <div class="send-order-details">
    <div v-if="sendDetails">
      <div class="row g-2 g-lg-3 align-items-stretch">
        <div class="col-xl-4">
          <div class="details-panel details-panel-summary h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="paper-plane" class="text-primary me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("dashboard.portfolio.selected_certificates_summary") }}
              </span>
            </div>

            <div class="summary-content">
              <div>
                <div class="text-muted small mb-1">
                  {{ $t("dashboard.portfolio.totalMwhToSend") }}
                </div>
                <div class="fs-2 fw-semibold lh-sm">
                  {{ totalSelectedMwh }}
                  <span class="fs-6 text-muted fw-normal">MWh</span>
                </div>
              </div>

              <div class="summary-detail">
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("dashboard.portfolio.selected_certificates") }}
                </div>
                <div class="summary-detail-value">
                  {{ selectedPortfolioItemsCount }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="details-panel h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="inbox" class="text-primary me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.send.recipient_information") }}
              </span>
            </div>

            <div class="row g-2">
              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.send.company_name") }}
                  </div>
                  <div class="fw-semibold">
                    {{ sendDetails.recipient_company_name ?? "-" }}
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.send.registry_country") }}
                  </div>
                  <span class="badge bg-secondary rounded-pill">
                    {{ sendDetails.recipient_registry_country ?? "-" }}
                  </span>
                </div>
              </div>

              <div :class="hasInternalAccountNumber ? 'col-md-6' : 'col-md-12'">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.send.eecs_account_id") }}
                  </div>
                  <div class="detail-value font-monospace">
                    {{ sendDetails.recipient_eecs_account_number ?? "-" }}
                  </div>
                </div>
              </div>

              <div
                v-if="sendDetails.recipient_registry_internal_account_number"
                class="col-md-6"
              >
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.send.internal_account_id") }}
                  </div>
                  <div class="detail-value font-monospace">
                    {{ sendDetails.recipient_registry_internal_account_number }}
                  </div>
                </div>
              </div>

              <div v-if="sendDetails.unit_price_pln" class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.send.unit_price_pln") }}
                  </div>
                  <div class="detail-value">
                    {{ sendDetails.unit_price_pln }} PLN/MWh
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Operation Notes Section -->
      <div v-if="sendDetails.operation_notes" class="details-panel mt-2">
        <div class="d-flex align-items-center mb-2">
          <FontAwesomeIcon icon="sticky-note" class="text-info me-2" />
          <span class="text-uppercase text-muted fw-semibold section-label">
            {{ $t("company_orders.send.operation_notes") }}
          </span>
        </div>
        <div class="detail-item">
          <p class="mb-0 small">
            {{ sendDetails.operation_notes }}
          </p>
        </div>
      </div>
    </div>

    <!-- Fallback to generic display -->
    <GenericOrderDetailsSection v-else :details="details" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { SendOrderDetails } from "../../common/companyOrderTypes";
import GenericOrderDetailsSection from "./GenericOrderDetailsSection.vue";

interface Props {
  details: Record<string, any> | null | undefined;
  totalSelectedMwh: number;
}

const props = defineProps<Props>();

// Computed
const sendDetails = computed((): SendOrderDetails | null => {
  if (!props.details) return null;

  // Type guard to check if details match SendOrderDetails structure
  if (
    Array.isArray(props.details.portfolio_items) &&
    (typeof props.details.recipient_company_name === "string" ||
      typeof props.details.recipient_registry_country === "string" ||
      typeof props.details.recipient_eecs_account_number === "string")
  ) {
    return props.details as SendOrderDetails;
  }

  return null;
});

const selectedPortfolioItemsCount = computed((): number => {
  return sendDetails.value?.portfolio_items.length ?? 0;
});

const hasInternalAccountNumber = computed((): boolean => {
  return !!sendDetails.value?.recipient_registry_internal_account_number;
});
</script>

<style scoped lang="scss">
@use "../order-details-shared";

.details-panel {
  height: 100%;
}

.details-panel-summary {
  background: var(--phoenix-primary-bg-subtle);
  border-color: var(--phoenix-primary-border-subtle);
}

.summary-detail-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--phoenix-primary-text-emphasis);
}
</style>
